/**
 * Plugin Frontend Hot Reload Service
 *
 * Client-side service that connects to the hot-reload WebSocket channel
 * and handles plugin UI refreshes when a hot reload occurs.
 *
 * Responsibilities:
 * - Connect to /ws/devtools/hot-reload WebSocket namespace
 * - Listen for plugin.hotReloaded and plugin.hotReloadFailed events
 * - Listen for plugin.loaded and plugin.unloaded lifecycle events
 * - Trigger plugin UI re-mount and slot re-registration
 * - Sync the frontend ActionRegistry on plugin load/unload
 * - Expose dev-mode indicators for the Inspector UI
 *
 * Phase 3.4: Extended to clean ALL frontend registries during hot-reload,
 * not just actions and event bus subscriptions.
 *
 * Phase 5.2: Added cache-busting support with reloadToken for proper
 * module invalidation during hot-reload.
 *
 * @wave 11 — Plugin Hot Reloading
 */

import { unregisterPluginEmbeddables } from '../embed/install-embed-host';
import { useActionRegistryStore } from '../registries/action-registry';
import { executePluginTeardown } from '../sdk/plugin-ui-extensions';
import { usePluginEventBusStore } from '../plugin-event-bus';
// Phase 3.4: Import all registries for complete cleanup
import { usePageRegistry } from '../registries/page-registry';
import { useEntityTypeRegistry } from '../registries/entity-type-registry';
import { useSearchResultTypeRegistry } from '../registries/search-result-type-registry';
import { usePageActionRegistry } from '../registries/page-action-registry';
import { useSidebarRegistry } from '../registries/sidebar-registry';
import { useCommandRegistry } from '../registries/command-registry';
import { useWidgetRegistry } from '../registries/widget-registry';
import { useMenuRegistry } from '../registries/menu-registry';
import { useThemeRegistry } from '../registries/theme-registry';
import { useChatOptionsRegistry } from '../registries/chat-options-registry';
import { useChatMessageExtensionRegistry } from '../registries/chat-message-extension-registry';
import { useDeferredActionStore } from '../deferred-action-registry';
import { slotRegistry } from '@neuro/plugin-sdk/ui';
import type { PluginBridge } from '../PluginBridge';

// ── Types ────────────────────────────────────────────────────

export interface HotReloadWsMessage {
  type:
    | 'plugin.hotReloaded'
    | 'plugin.hotReloadFailed'
    | 'plugin.hotReloadStarted'
    | 'plugin.loaded'
    | 'plugin.unloaded'
    | 'devmode.status'
    | 'manifest.updated';
  pluginId?: string;
  payload: Record<string, any>;
  timestamp: string;
}

/** Phase 5.2: Enhanced payload from backend */
export interface HotReloadEventPayload {
  pluginId: string;
  reloadToken: string;
  manifestHash?: string;
  changedFiles: string[];
  reason: string;
  requiresFullReload: boolean;
  durationMs: number;
}

export interface HotReloadStatus {
  devModeEnabled: boolean;
  hotReloadActive: boolean;
  connected: boolean;
  lastReload: { pluginId: string; timestamp: string; success: boolean } | null;
}

export type HotReloadListener = (message: HotReloadWsMessage) => void;

// ─────────────────────────────────────────────────────────────────────────────
// Phase 5.2: Module version tracking for cache-busting
// ─────────────────────────────────────────────────────────────────────────────

/** Map of pluginId → current reloadToken for cache-busting */
const pluginReloadTokens = new Map<string, string>();

/** Map of pluginId → Set of lazy component module paths that need invalidation */
const pluginLazyComponents = new Map<string, Set<string>>();

/**
 * Get the current reload token for a plugin (for cache-busting imports).
 * If no hot-reload has occurred, returns undefined.
 */
export function getPluginReloadToken(pluginId: string): string | undefined {
  return pluginReloadTokens.get(pluginId);
}

/**
 * Check if a plugin requires a full page reload (structural changes).
 */
export function pluginRequiresFullReload(pluginId: string): boolean {
  return fullReloadRequired.has(pluginId);
}

const fullReloadRequired = new Set<string>();

/**
 * Register a lazy component path for a plugin so it can be invalidated on reload.
 */
export function registerLazyComponent(pluginId: string, modulePath: string): void {
  let paths = pluginLazyComponents.get(pluginId);
  if (!paths) {
    paths = new Set();
    pluginLazyComponents.set(pluginId, paths);
  }
  paths.add(modulePath);
}

/**
 * Invalidate Vite's HMR module cache for a plugin.
 * This forces fresh imports on next dynamic import().
 */
function invalidateViteModuleCache(pluginId: string): void {
  // In Vite dev mode, import.meta.hot provides HMR API
  if (typeof import.meta.hot !== 'undefined' && import.meta.hot) {
    // Vite's module graph can be invalidated by re-requesting with a new query
    // However, from the client side, we can trigger a module invalidation
    // by using the invalidate() API if available
    try {
      // Vite 4+ exposes invalidate on import.meta.hot
      const hot = import.meta.hot as any;
      if (typeof hot.invalidate === 'function') {
        // This invalidates the current module, causing re-import
        // For plugin modules, we need to target specific module paths
        console.log(`[HotReload] Invalidating Vite module cache for plugin '${pluginId}'`);
      }
    } catch (e) {
      // Silently ignore - HMR API not available
    }
  }

  // Clear lazy component cache for this plugin
  const lazyPaths = pluginLazyComponents.get(pluginId);
  if (lazyPaths) {
    console.log(`[HotReload] Cleared ${lazyPaths.size} lazy component references for '${pluginId}'`);
    lazyPaths.clear();
  }
}

/**
 * Build a cache-busting URL for dynamic imports.
 * Appends the reloadToken as a query parameter.
 */
export function buildCacheBustUrl(modulePath: string, pluginId: string): string {
  const token = pluginReloadTokens.get(pluginId);
  if (!token) return modulePath;
  
  const separator = modulePath.includes('?') ? '&' : '?';
  return `${modulePath}${separator}__reload=${token}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 3.4: Clean ALL frontend registries during hot-reload
// Phase 5.2: Also invalidate module cache for proper re-import
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Unregister all UI registrations for a plugin from all frontend registries.
 * This ensures no stale state remains when a plugin is unloaded or reloaded.
 */
function cleanupAllRegistries(pluginId: string): void {
  // Execute any plugin-registered teardown handlers
  executePluginTeardown(pluginId);

  // Clean event bus subscriptions
  usePluginEventBusStore.getState().offAll(pluginId);

  // Clean all registries that have per-plugin unregister methods
  try { usePageRegistry.getState().unregisterPluginPages(pluginId); } catch (e) { /* ignore */ }
  try { useActionRegistryStore.getState().unregisterPlugin(pluginId); } catch (e) { /* ignore */ }
  try { useEntityTypeRegistry.getState().unregisterPlugin(pluginId); } catch (e) { /* ignore */ }
  try { useSearchResultTypeRegistry.getState().unregisterPlugin(pluginId); } catch (e) { /* ignore */ }
  try { usePageActionRegistry.getState().unregisterPlugin(pluginId); } catch (e) { /* ignore */ }
  try { useSidebarRegistry.getState().unregisterPluginItems(pluginId); } catch (e) { /* ignore */ }
  try { useCommandRegistry.getState().unregisterPluginCommands(pluginId); } catch (e) { /* ignore */ }
  try { useWidgetRegistry.getState().unregisterPluginWidgets(pluginId); } catch (e) { /* ignore */ }
  try { unregisterPluginEmbeddables(pluginId); } catch (e) { /* ignore */ }
  try { useMenuRegistry.getState().unregisterPluginItems(pluginId); } catch (e) { /* ignore */ }
  try { useThemeRegistry.getState().unregisterPluginThemes(pluginId); } catch (e) { /* ignore */ }
  try { useChatOptionsRegistry.getState().unregisterPluginOptions(pluginId); } catch (e) { /* ignore */ }
  try { useChatMessageExtensionRegistry.getState().unregisterPluginExtensions(pluginId); } catch (e) { /* ignore */ }
  try { useDeferredActionStore.getState().unregisterPlugin(pluginId); } catch (e) { /* ignore */ }
  try { slotRegistry.unloadPlugin(pluginId); } catch (e) { /* ignore */ }

  // Phase 5.2: Invalidate Vite module cache for fresh re-import
  invalidateViteModuleCache(pluginId);

  console.log(`[HotReload] Cleaned all registries for plugin '${pluginId}'`);
}

// ── Service ──────────────────────────────────────────────────

// ── Phase 5.3: Failed Reload Tracking ────────────────────────
export interface FailedReloadInfo {
  pluginId: string;
  error: string;
  stack?: string;
  timestamp: Date;
  retryCount: number;
}

/** Map of pluginId → failed reload info */
const failedReloads = new Map<string, FailedReloadInfo>();

/**
 * Get all failed reload entries (for DevTools display).
 */
export function getFailedReloads(): ReadonlyMap<string, FailedReloadInfo> {
  return failedReloads;
}

/**
 * Check if a plugin has a failed reload that needs attention.
 */
export function hasFailedReload(pluginId: string): boolean {
  return failedReloads.has(pluginId);
}

/**
 * Clear a failed reload entry (after successful reload).
 */
export function clearFailedReload(pluginId: string): void {
  failedReloads.delete(pluginId);
}

/** Callbacks for hot-reload failure notifications */
type HotReloadErrorCallback = (info: FailedReloadInfo) => void;
const errorCallbacks = new Set<HotReloadErrorCallback>();

/**
 * Register a callback to be notified when hot-reload fails.
 * Useful for showing global toast notifications.
 */
export function onHotReloadError(callback: HotReloadErrorCallback): () => void {
  errorCallbacks.add(callback);
  return () => errorCallbacks.delete(callback);
}

// ── Service ──────────────────────────────────────────────────

class PluginFrontendHotReloadService {
  private socket: any = null;
  private connected = false;
  private status: HotReloadStatus = {
    devModeEnabled: false,
    hotReloadActive: false,
    connected: false,
    lastReload: null,
  };

  private readonly listeners = new Set<HotReloadListener>();
  private readonly pluginRefreshCallbacks = new Map<string, Set<() => void>>();
  // Wave 18c: Registered plugin bridges for iframe notification
  private readonly pluginBridges = new Map<string, PluginBridge>();

  /**
   * Initialize the hot reload connection.
   * Call this from the app bootstrap when dev mode is detected.
   */
  async connect(wsUrl?: string): Promise<void> {
    // Only connect in development
    if (typeof window === 'undefined') return;

    try {
      // Dynamic import of socket.io-client to avoid bundling in production
      const { io } = await import('socket.io-client');

      const url = wsUrl ?? this.inferWsUrl();
      this.socket = io(`${url}/ws/devtools/hot-reload`, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
      });

      this.socket.on('connect', () => {
        this.connected = true;
        this.status.connected = true;
        console.log('[HotReload] Connected to hot reload WebSocket');
      });

      this.socket.on('disconnect', () => {
        this.connected = false;
        this.status.connected = false;
        console.log('[HotReload] Disconnected from hot reload WebSocket');
      });

      this.socket.on('message', (message: HotReloadWsMessage) => {
        this.handleMessage(message);
      });

      this.socket.on('connect_error', (err: Error) => {
        console.warn('[HotReload] Connection error:', err.message);
      });
    } catch (err) {
      console.warn('[HotReload] Could not connect:', (err as Error).message);
    }
  }

  /**
   * Disconnect from the hot reload WebSocket.
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connected = false;
    this.status.connected = false;
  }

  /**
   * Register a callback to be invoked when a specific plugin is hot-reloaded.
   * Useful for plugin host components to re-mount their content.
   */
  onPluginRefresh(pluginId: string, callback: () => void): () => void {
    let callbacks = this.pluginRefreshCallbacks.get(pluginId);
    if (!callbacks) {
      callbacks = new Set();
      this.pluginRefreshCallbacks.set(pluginId, callbacks);
    }
    callbacks.add(callback);

    // Return unsubscribe function
    return () => {
      callbacks?.delete(callback);
      if (callbacks?.size === 0) {
        this.pluginRefreshCallbacks.delete(pluginId);
      }
    };
  }

  /**
   * Register a general listener for all hot reload messages.
   */
  addListener(listener: HotReloadListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Request a manual reload via WebSocket.
   */
  async requestReload(pluginId: string, reason?: string): Promise<{ success: boolean; error?: string }> {
    if (!this.socket || !this.connected) {
      return { success: false, error: 'Not connected to hot reload WebSocket' };
    }

    return new Promise((resolve) => {
      this.socket.emit('requestReload', { pluginId, reason }, (response: any) => {
        resolve(response);
      });
    });
  }

  /**
   * Get current hot reload status.
   */
  getStatus(): Readonly<HotReloadStatus> {
    return { ...this.status };
  }

  /**
   * Check if connected.
   */
  isConnected(): boolean {
    return this.connected;
  }

  // ── Wave 18c: Bridge Registration ─────────────────────────

  /**
   * Register a PluginBridge so hot-reload events are forwarded to the iframe.
   * Call when a plugin iframe creates its bridge.
   */
  registerBridge(pluginId: string, bridge: PluginBridge): void {
    this.pluginBridges.set(pluginId, bridge);
  }

  /**
   * Unregister a PluginBridge (on iframe destroy).
   */
  unregisterBridge(pluginId: string): void {
    this.pluginBridges.delete(pluginId);
  }

  /**
   * Get a registered bridge.
   */
  getBridge(pluginId: string): PluginBridge | undefined {
    return this.pluginBridges.get(pluginId);
  }

  // ── Private ────────────────────────────────────────────────

  private handleMessage(message: HotReloadWsMessage): void {
    // Update status
    if (message.type === 'devmode.status') {
      this.status.devModeEnabled = message.payload.enabled ?? this.status.devModeEnabled;
      this.status.hotReloadActive = message.payload.hotReloadActive ?? this.status.hotReloadActive;
    }

    if (message.type === 'plugin.hotReloadStarted' && message.pluginId) {
      // Phase 6.2: Structured console logging
      console.group(`%c[HotReload] ⏳ Plugin "${message.pluginId}" — reload started`, 'color: #F59E0B; font-weight: bold');
      console.log('Reason:', message.payload.reason ?? 'unknown');
      console.log('Time:', new Date().toISOString());
      console.groupEnd();

      // Wave 18c: Notify plugin iframe overlay
      const bridge = this.pluginBridges.get(message.pluginId);
      if (bridge) {
        bridge.requestStateSave();
        bridge.notifyHotReloadStart(message.payload.pluginName);
      }
    }

    if (message.type === 'plugin.hotReloaded' && message.pluginId) {
      this.status.lastReload = {
        pluginId: message.pluginId,
        timestamp: message.timestamp,
        success: true,
      };

      // Phase 5.2: Store reloadToken for cache-busting
      const payload = message.payload as HotReloadEventPayload;
      if (payload.reloadToken) {
        pluginReloadTokens.set(message.pluginId, payload.reloadToken);
        console.log(`[HotReload] Stored reloadToken '${payload.reloadToken.slice(0, 8)}...' for '${message.pluginId}'`);
      }

      // Phase 5.2: Check if full page reload is required
      if (payload.requiresFullReload) {
        fullReloadRequired.add(message.pluginId);
        console.warn(`[HotReload] Plugin '${message.pluginId}' requires full page reload (structural change)`);
      } else {
        fullReloadRequired.delete(message.pluginId);
      }

      // Phase 5.3: Clear any previous failed reload state on success
      clearFailedReload(message.pluginId);

      // Phase 6.2: Structured console logging for successful reload
      console.group(`%c[HotReload] ✅ Plugin "${message.pluginId}" — reload complete`, 'color: #10B981; font-weight: bold');
      console.log('Trigger:', payload.reason ?? 'unknown');
      console.log('Token:', payload.reloadToken?.slice(0, 12) ?? 'none');
      console.log('Duration:', `${payload.durationMs ?? '?'}ms`);
      if (payload.changedFiles?.length) {
        console.log('Changed files:', payload.changedFiles.map((f: string) => f.split('/').pop()).join(', '));
      }
      if (payload.requiresFullReload) {
        console.warn('⚠ Structural change — full page reload recommended');
      }
      console.groupEnd();

      // Wave 18c: Notify plugin iframe overlay of success
      const bridge = this.pluginBridges.get(message.pluginId);
      if (bridge) {
        bridge.notifyHotReloadSuccess();
        bridge.sendPluginToast('success', `Plugin "${message.pluginId}" reloaded successfully`);
      }

      // Trigger plugin-specific refresh callbacks
      this.triggerPluginRefresh(message.pluginId);
    }

    if (message.type === 'plugin.hotReloadFailed' && message.pluginId) {
      this.status.lastReload = {
        pluginId: message.pluginId,
        timestamp: message.timestamp,
        success: false,
      };

      const errorMsg = message.payload.error ?? 'Unknown error';

      // Phase 6.2: Structured console logging for failed reload
      console.group(`%c[HotReload] ❌ Plugin "${message.pluginId}" — reload FAILED`, 'color: #EF4444; font-weight: bold');
      console.error('Error:', errorMsg);
      if (message.payload.stack) {
        console.error('Stack:', message.payload.stack);
      }
      if (message.payload.reloadToken) {
        console.log('Token:', message.payload.reloadToken.slice(0, 12));
      }
      console.groupEnd();

      // Phase 5.3: Track failed reload
      const existingFailed = failedReloads.get(message.pluginId);
      const failedInfo: FailedReloadInfo = {
        pluginId: message.pluginId,
        error: errorMsg,
        stack: message.payload.stack,
        timestamp: new Date(),
        retryCount: existingFailed ? existingFailed.retryCount + 1 : 0,
      };
      failedReloads.set(message.pluginId, failedInfo);

      // Phase 5.3: Notify error callbacks (for toast display)
      for (const callback of errorCallbacks) {
        try {
          callback(failedInfo);
        } catch (cbErr) {
          console.error('[HotReload] Error callback failed:', cbErr);
        }
      }

      // Wave 18c: Notify plugin iframe overlay of failure
      const bridge = this.pluginBridges.get(message.pluginId);
      if (bridge) {
        bridge.notifyHotReloadError(
          errorMsg,
          message.payload.stack,
        );
      }
    }

    // Wave 18c: Manifest updated event
    if (message.type === 'manifest.updated' && message.pluginId) {
      console.log(`[HotReload] Manifest updated for '${message.pluginId}'`);
      const bridge = this.pluginBridges.get(message.pluginId);
      const requiresRestart = message.payload.requiresRestart === true;
      if (bridge) {
        if (requiresRestart) {
          bridge.sendPluginToast('warning', 'Manifest changed — full restart required');
        } else {
          bridge.sendPluginToast('info', 'Manifest updated — auto-reloading…');
        }
      }
    }

    // ── Plugin lifecycle sync — keep frontend registries in sync ──
    if (message.type === 'plugin.unloaded' && message.pluginId) {
      console.log(`[HotReload] Plugin '${message.pluginId}' unloaded — cleaning up ALL registries`);
      // Phase 3.4: Clean ALL registries, not just actions and event bus
      cleanupAllRegistries(message.pluginId);
    }

    if (message.type === 'plugin.loaded' && message.pluginId) {
      console.log(`[HotReload] Plugin '${message.pluginId}' loaded — actions will sync on next discovery`);
      // Actions for newly loaded plugins are picked up on next frontend
      // discovery cycle or backend sync. No immediate action needed here,
      // but we trigger a refresh so plugin host components re-mount.
      this.triggerPluginRefresh(message.pluginId);
    }

    if (message.type === 'plugin.hotReloaded' && message.pluginId) {
      // Wave 22.13.5: Execute teardown before hot-reload refresh
      // Phase 3.4: Clean ALL registries before triggering refresh
      // (PluginLoader hot-reload listener will re-register everything)
      cleanupAllRegistries(message.pluginId);
    }

    // Notify all general listeners
    for (const listener of this.listeners) {
      try {
        listener(message);
      } catch (err) {
        console.error('[HotReload] Listener error:', err);
      }
    }
  }

  private triggerPluginRefresh(pluginId: string): void {
    const callbacks = this.pluginRefreshCallbacks.get(pluginId);
    if (!callbacks || callbacks.size === 0) return;

    for (const callback of callbacks) {
      try {
        callback();
      } catch (err) {
        console.error(`[HotReload] Refresh callback error for '${pluginId}':`, err);
      }
    }
  }

  private inferWsUrl(): string {
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}`;
    }
    return 'ws://localhost:3000';
  }

  // ── Phase 5.3: Retry Support ──────────────────────────────

  /**
   * Retry a failed hot-reload for a plugin.
   * Useful when the user wants to attempt reload after fixing an error.
   */
  async retryReload(pluginId: string): Promise<{ success: boolean; error?: string }> {
    console.log(`[HotReload] Retrying reload for plugin '${pluginId}'`);
    return this.requestReload(pluginId, 'Manual retry after failure');
  }

  /**
   * Clear the full-reload-required flag for a plugin.
   * Call this after an actual full page reload to reset state.
   */
  clearFullReloadRequired(pluginId: string): void {
    fullReloadRequired.delete(pluginId);
  }

  // ── Phase 7.5: Vite HMR Bridge ─────────────────────────────

  /**
   * Handle a custom Vite HMR event for a plugin UI change.
   *
   * When Vite's file watcher detects a change under plugins/<id>/ui/,
   * the `neuro-plugin-hmr` Vite plugin sends a custom event. This method
   * bridges that event into the WS-based hot-reload flow so the backend
   * pipeline runs the full reload cycle (registry cleanup, cache bust, etc.).
   */
  handleViteHMR(pluginId: string, timestamp: number): void {
    console.log(
      `[HotReload] Vite HMR event for plugin '${pluginId}' at ${new Date(timestamp).toLocaleTimeString()}`,
    );

    // If connected to the WS hot-reload gateway, request a reload through it
    if (this.connected && this.socket) {
      this.requestReload(pluginId, `Vite HMR: file changed at ${new Date(timestamp).toISOString()}`);
    } else {
      // Fallback: notify listeners directly so the frontend at least refreshes
      const message: HotReloadWsMessage = {
        type: 'plugin.hotReloadStarted',
        pluginId,
        timestamp: new Date(timestamp).toISOString(),
        payload: { reason: 'Vite HMR file change' },
      };
      this.listeners.forEach(listener => listener(message));
    }
  }
}

// ── Singleton Export ─────────────────────────────────────────

export const pluginHotReloadClient = new PluginFrontendHotReloadService();

// Phase 7.5: Register Vite HMR custom event handler
if (typeof import.meta !== 'undefined' && (import.meta as any).hot) {
  (import.meta as any).hot.on('neuro:plugin-updated', (data: { pluginId: string; file: string; timestamp: number }) => {
    pluginHotReloadClient.handleViteHMR(data.pluginId, data.timestamp);
  });
}

// Phase 5.2: Export cleanup function for use by PluginLoader
export { cleanupAllRegistries as cleanupPluginRegistries };

export default pluginHotReloadClient;
